<template>
  <div class="page-container dashboard">
    <!-- 顶部问候 -->
    <div class="dash-head">
      <div class="dash-head-left">
        <h2 class="dash-title">国塔文化管理系统</h2>
        <p class="dash-sub">{{ greeting }}，今天是 {{ todayText }}</p>
      </div>
    </div>

    <!-- 统计卡 -->
    <el-row :gutter="16" class="stat-row">
      <el-col :span="statSpan" v-for="card in statCards" :key="card.label">
        <el-card shadow="never" class="stat-card">
          <div class="stat-inner">
            <div class="stat-icon" :style="{ background: card.bg }">
              <i :class="card.icon"></i>
            </div>
            <p class="stat-num">{{ card.num }}</p>
            <p class="stat-label">{{ card.label }}</p>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 第二行：最新预约 -->
    <el-row :gutter="16">
      <el-col :span="24">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head">
            <span>最新预约</span>
            <span class="chart-tip">最近 {{ latestAppointments.length }} 条，未分配的需尽快安排接待人</span>
          </div>
          <div class="appoint-list">
            <div class="appoint-item" v-for="a in latestAppointments" :key="a.id">
              <div class="appoint-cell appoint-main">
                <span class="appoint-name">{{ a.name }}</span>
                <el-tag size="mini" :type="appointTypeTagType(a.type)">{{ a.type_text }}</el-tag>
                <span class="appoint-people">{{ a.people_count }} 人</span>
              </div>
              <div class="appoint-cell appoint-date">
                <i class="el-icon-date"></i>{{ a.appoint_date }}
              </div>
              <div class="appoint-cell appoint-staff" :class="{ unassigned: !a.service_id }">
                <template v-if="a.service_id">
                  <i class="el-icon-user"></i>{{ a.service_name }} · {{ a.service_position }}
                </template>
                <template v-else>未分配接待人</template>
              </div>
              <div class="appoint-cell appoint-status">
                <el-tag size="mini" :type="appointStatusTagType(a.status)">{{ shortStatus(a.status_text) }}</el-tag>
              </div>
            </div>
            <div class="list-empty" v-if="!latestAppointments.length">暂无预约</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 第三行：趋势 + 预约状态 -->
    <el-row :gutter="16" class="chart-row">
      <el-col :span="16">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head">
            <span>近 30 日新增趋势</span>
            <span class="chart-tip">用户注册与参观预约</span>
          </div>
          <div ref="trendChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head"><span>预约状态分布</span></div>
          <div ref="appointChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 第四行：地区 / 核销 / 社区 -->
    <el-row :gutter="16" class="chart-row">
      <el-col :span="8">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head"><span>用户地区分布</span></div>
          <div ref="regionChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head">
            <span>核销失败原因</span>
            <span class="chart-tip">共 {{ verifyTotal }} 次核销，成功 {{ verifySuccess }} 次</span>
          </div>
          <div ref="failChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-head"><span>社区内容构成</span></div>
          <div ref="postChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getHomeOverview, getLatestAppointments } from '@/api/home'
import { getReportOverview } from '@/api/report'
// 状态/类型 → 标签色（纯 UI 映射，与预约列表页同源；后端只下发中文文案）
import { appointStatusTagType, appointTypeTagType } from '@/mock/appointment'

// 统计卡 UI 元信息：后端只给 key/label/value，图标与配色在前端
const STAT_META = {
  user_total: { icon: 'el-icon-user-solid', bg: '#8E1103' },
  appointment_today: { icon: 'el-icon-date', bg: '#B8860B' },
  auth_pending: { icon: 'el-icon-postcard', bg: '#C8A464' },
  verify_success: { icon: 'el-icon-circle-check', bg: '#5B7065' },
  post_total: { icon: 'el-icon-chat-dot-round', bg: '#7A6A53' },
  activity_ongoing: { icon: 'el-icon-flag', bg: '#3D2B1F' }
}

export default {
  name: 'HomePage',
  data() {
    return {
      charts: [],
      statCards: [],
      latestAppointments: [],
      verifyTotal: 0,
      verifySuccess: 0
    }
  },
  computed: {
    // 主账号口径：超管（is_super=1）或主公司（company_id=1，固定）→ 看全部
    isMainAccount() {
      const info = (this.$store && this.$store.getters && this.$store.getters.getUserInfo) || {}
      return Number(info.is_super) === 1 || Number(info.company_id) === 1
    },
    // 统计卡列宽：主账号 6 张（span 4），企业账号只显示 3 张（span 8），按实际张数均分不留白
    statSpan() {
      const n = this.statCards.length || 6
      return Math.max(4, Math.floor(24 / n))
    },
    greeting() {
      const h = new Date().getHours()
      if (h < 6) return '夜深了'
      if (h < 12) return '早上好'
      if (h < 14) return '中午好'
      if (h < 18) return '下午好'
      return '晚上好'
    },
    todayText() {
      const d = new Date()
      const week = '日一二三四五六'[d.getDay()]
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 星期${week}`
    }
  },
  mounted() {
    this.loadOverview()
    this.loadLatestAppointments()
    this.loadReport()
    this.handleResize = () => {
      this.charts.forEach(c => c.resize())
    }
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.charts.forEach(c => c.dispose())
    this.charts = []
  },
  methods: {
    // 供模板使用的取值函数（import 的函数模板里访问不到，必须挂实例）
    appointTypeTagType,
    appointStatusTagType,
    // 状态文案太长的展示时缩短（如「待补录到达时间」）
    shortStatus(text) {
      return text === '待补录到达时间' ? '待补录' : (text || '')
    },
    // 统计卡：接口固定给 6 张，前端按账号类型决定露哪几张
    // 企业账号隐藏「实名待审 / 社区帖子 / 进行中活动」——这三类由平台方管，不属于企业经营口径
    loadOverview() {
      getHomeOverview().then(res => {
        if (res.code !== 200) return
        const all = (res.data && res.data.cards) || []
        const hidden = ['auth_pending', 'post_total', 'activity_ongoing']
        const cards = this.isMainAccount ? all : all.filter(c => hidden.indexOf(c.key) === -1)
        this.statCards = cards.map(c => {
          const meta = STAT_META[c.key] || {}
          return {
            label: c.label,
            num: c.value,
            icon: meta.icon || 'el-icon-menu',
            bg: meta.bg || '#8a7a66'
          }
        })
      })
    },
    // 最新预约（后端已按提交时间倒序取 6 条）
    loadLatestAppointments() {
      getLatestAppointments().then(res => {
        if (res.code !== 200) return
        this.latestAppointments = (res.data && res.data.list) || []
      })
    },
    // 报表：5 组图表数据一次拿到，再逐张渲染
    loadReport() {
      getReportOverview().then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.verifyTotal = (data.verify && data.verify.total) || 0
        this.verifySuccess = (data.verify && data.verify.success) || 0
        this.$nextTick(() => {
          this.renderTrend(data.trend || {})
          this.renderAppoint(data.appointment_status || [])
          this.renderRegion(data.user_region || [])
          this.renderFail((data.verify && data.verify.fail) || [])
          this.renderPost(data.post_type || [])
        })
      })
    },
    makeChart(refName, option) {
      const el = this.$refs[refName]
      if (!el) return
      const chart = this.$echarts.init(el)
      chart.setOption(option)
      this.charts.push(chart)
    },
    renderTrend(trend) {
      const labels = (trend.dates || []).map(d => d.slice(5)) // YYYY-MM-DD → MM-DD

      this.makeChart('trendChart', {
        color: ['#8E1103', '#C8A464'],
        tooltip: { trigger: 'axis' },
        legend: { data: ['新增用户', '新增预约'], top: 0, right: 0, itemWidth: 14, textStyle: { color: '#8a7a66' } },
        grid: { left: 8, right: 8, top: 36, bottom: 0, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: labels, axisLine: { lineStyle: { color: '#d8cfc0' } }, axisLabel: { color: '#8a7a66' } },
        yAxis: { type: 'value', minInterval: 1, axisLine: { show: false }, splitLine: { lineStyle: { color: '#efe8dc' } }, axisLabel: { color: '#8a7a66' } },
        series: [
          {
            name: '新增用户', type: 'line', smooth: true, symbolSize: 5, data: trend.user_counts || [],
            areaStyle: { opacity: 0.08 }, lineStyle: { width: 2 }
          },
          { name: '新增预约', type: 'bar', barMaxWidth: 14, itemStyle: { borderRadius: [3, 3, 0, 0] }, data: trend.appointment_counts || [] }
        ]
      })
    },
    renderAppoint(list) {
      const data = list
        .filter(d => d.value > 0)
        .map(d => ({
          name: d.label === '待补录到达时间' ? '待补录' : d.label,
          value: d.value
        }))

      this.makeChart('appointChart', {
        color: ['#8E1103', '#C8A464', '#5B7065', '#A89880', '#3D2B1F'],
        tooltip: { trigger: 'item', formatter: '{b}：{c} 条（{d}%）' },
        legend: { orient: 'vertical', right: 4, top: 'center', textStyle: { color: '#8a7a66' } },
        series: [{
          type: 'pie', radius: ['48%', '72%'], center: ['38%', '50%'],
          label: { show: false },
          data
        }]
      })
    },
    renderRegion(list) {
      // 后端已按省份降序取前 N；reverse 后 yAxis 自上而下由多到少
      const pairs = list.map(i => [i.province, i.value]).reverse()

      this.makeChart('regionChart', {
        color: ['#8E1103'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 8, right: 24, top: 8, bottom: 0, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#efe8dc' } }, axisLabel: { color: '#8a7a66' } },
        yAxis: { type: 'category', data: pairs.map(p => p[0]), axisLine: { lineStyle: { color: '#d8cfc0' } }, axisLabel: { color: '#5f5346' } },
        series: [{
          type: 'bar', barMaxWidth: 14, data: pairs.map(p => p[1]),
          itemStyle: { borderRadius: [0, 3, 3, 0], color: '#8E1103', opacity: 0.85 },
          label: { show: true, position: 'right', color: '#8a7a66' }
        }]
      })
    },
    renderFail(failList) {
      const pairs = failList.map(i => [i.reason, i.value]).reverse()

      this.makeChart('failChart', {
        color: ['#B8860B'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 8, right: 24, top: 8, bottom: 0, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#efe8dc' } }, axisLabel: { color: '#8a7a66' } },
        yAxis: { type: 'category', data: pairs.map(p => p[0]), axisLine: { lineStyle: { color: '#d8cfc0' } }, axisLabel: { color: '#5f5346' } },
        series: [{
          type: 'bar', barMaxWidth: 14, data: pairs.map(p => p[1]),
          itemStyle: { borderRadius: [0, 3, 3, 0], color: '#B8860B', opacity: 0.85 },
          label: { show: true, position: 'right', color: '#8a7a66' }
        }]
      })
    },
    renderPost(list) {
      const data = list
        .filter(d => d.value > 0)
        .map(d => ({ name: d.label, value: d.value }))

      this.makeChart('postChart', {
        color: ['#8E1103', '#C8A464', '#5B7065'],
        tooltip: { trigger: 'item', formatter: '{b}：{c} 篇（{d}%）' },
        legend: { bottom: 0, textStyle: { color: '#8a7a66' }, itemWidth: 14 },
        series: [{
          type: 'pie', radius: ['42%', '66%'], center: ['50%', '44%'],
          label: { formatter: '{d}%', color: '#f7f2e9', position: 'inside' },
          data
        }]
      })
    }
  }
}
</script>

<style scoped>
.dashboard {
  display: block; /* 覆盖 page-container 的 flex 纵向：内容按自然高度排，超出由本页滚动 */
  overflow-y: auto;
  padding: 20px;
}

/* ===== 顶部 ===== */
.dash-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
}

.dash-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: var(--text);
}

.dash-sub {
  margin: 0;
  font-size: 13px;
  color: var(--text-200);
}

/* ===== 统计卡 ===== */
.stat-row {
  margin-bottom: 16px;
}

.stat-card >>> .el-card__body {
  padding: 18px 16px;
}

.stat-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
}

.stat-icon {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f7f2e9;
  font-size: 19px;
}

.stat-num {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--text);
}

.stat-label {
  margin: 0;
  font-size: 12px;
  color: var(--text-200);
  white-space: nowrap;
}

/* ===== 最新预约 ===== */
.appoint-list {
  display: flex;
  flex-direction: column;
}

.appoint-item {
  display: flex;
  align-items: center;
  padding: 11px 4px;
  border-bottom: 1px solid #f2ecdf;
  font-size: 13px;
  color: var(--text);
}

.appoint-item:last-child {
  border-bottom: none;
}

.list-empty {
  padding: 28px 0;
  text-align: center;
  font-size: 13px;
  color: var(--text-200);
}

.appoint-main {
  flex: 1 1 30%;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.appoint-name {
  font-weight: 600;
}

.appoint-people {
  color: var(--text-200);
  font-size: 12px;
}

.appoint-date {
  flex: 0 0 150px;
  color: var(--text-200);
}

.appoint-date i {
  margin-right: 5px;
}

.appoint-staff {
  flex: 1 1 30%;
  color: #5B7065;
}

.appoint-staff.unassigned {
  color: #b02020;
  font-weight: 600;
}

.appoint-staff i {
  margin-right: 5px;
}

.appoint-status {
  flex: 0 0 80px;
  text-align: right;
}

/* ===== 图表卡 ===== */
.chart-card >>> .el-card__header {
  padding: 14px 16px;
}

.chart-card >>> .el-card__body {
  padding: 12px 16px 16px;
}

.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-weight: 600;
  color: var(--text);
}

.chart-tip {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-200);
}

.chart-box {
  width: 100%;
  height: 300px;
}

.chart-row {
  margin-top: 16px;
}
</style>
